/* eslint-disable no-nested-ternary */
import {
  Box,
  Container,
  Dialog,
  dialogClasses,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';

import history from '@history';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { popModal } from 'app/store/modalSlice';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

const Transition = (slide) =>
  React.forwardRef((props, ref) => <Slide timeout={275} direction={slide} ref={ref} {...props} />);

const ModalComponent = ({
  onOpen,
  index,
  onClose,
  open: isOpenInStore,
  slideDirection,
  title,
  subTitle,
  closeIcon,
  maxWidth = 'xs',
  scroll = 'body',
  fullScreen = false,
  body,
  showCloseIcon = true,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const themeConfog = useTheme();
  const isMobile = useMediaQuery(themeConfog.breakpoints.down('md'));
  const root = document.querySelector('body');

  const handleOpen = React.useCallback(() => {
    if (onOpen) onOpen();
    // eslint-disable-next-line no-unused-expressions
    root && root.classList.add('actionsheet-open');

    setTimeout(() => setOpen(true), 0);
    const params = queryString.parse(history.location.search);
    const hasWrongParam = params.modal && +params.modal >= index + 1;
    history[hasWrongParam ? 'replace' : 'push'](
      `?${queryString.stringify({ ...params, modal: index + 1 })}`
    );
  }, [index, onOpen]);

  const clearFromStack = React.useCallback(
    (_, reason) => {
      if (reason && reason === 'backdropClick') return;
      setTimeout(() => dispatch(popModal()), 200);
      // eslint-disable-next-line no-unused-expressions
      root && root.classList.remove('actionsheet-open');
    },
    [dispatch]
  );

  const handleClose = React.useCallback(() => {
    if (onClose) onClose();
    setOpen(false);
    clearFromStack();
  }, [clearFromStack, onClose]);

  React.useEffect(() => {
    if (isOpenInStore) {
      handleOpen();
    } else {
      handleClose();
    }
  }, [handleClose, handleOpen, isOpenInStore]);

  const TransitionComponent = React.useMemo(
    () => (fullScreen ? Transition(slideDirection) : undefined),
    [fullScreen, slideDirection]
  );

  const closeIconButton = React.useMemo(
    () => (
      <IconButton
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          left: theme.spacing(2),
          top: theme.spacing(2),
          transform: 'translatey(-10%)',
          fontSize: 23,
          zIndex: 1301 + index,
        })}
      >
        {closeIcon ||
          (slideDirection === 'right' ? (
            <FuseSvgIcon>HiOutlineChevronLeft</FuseSvgIcon>
          ) : slideDirection === 'up' ? (
            <FuseSvgIcon>HiOutlineChevronLeft</FuseSvgIcon>
          ) : slideDirection === 'left' ? (
            <FuseSvgIcon>HiOutlineChevronLeft</FuseSvgIcon>
          ) : (
            <FuseSvgIcon>HiOutlineChevronLeft</FuseSvgIcon>
          ))}
      </IconButton>
    ),
    [handleClose, closeIcon, index, slideDirection]
  );
  return (
    <Dialog
      open={open}
      onClose={clearFromStack}
      fullWidth
      PaperProps={{ sx: { borderRadius: 0 } }}
      maxWidth={maxWidth}
      scroll={scroll}
      fullScreen={fullScreen}
      TransitionComponent={TransitionComponent}
      sx={(theme) => ({
        '&': {
          zIndex: 1300 + index,
        },
        [`& .${dialogClasses.paper}`]: {
          overflow: isMobile ? 'auto' : 'visible',
          borderRadius: fullScreen ? 0 : undefined,
          backgroundColor: fullScreen
            ? theme.palette.background.default
            : theme.palette.background.paper,
        },
      })}
    >
      {fullScreen ? (
        <Box
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
        >
          {title ? (
            <Box>
              <Box
                sx={{
                  minHeight: 'calc(64px + env(safe-area-inset-top))',
                  display: 'flex',
                  justifyContent: 'centeer',
                  alignItems: 'center',
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                }}
              >
                <DialogTitle sx={{ width: '100%' }}>
                  {showCloseIcon && closeIconButton}
                  {title}
                  {subTitle && (
                    <Typography component="span" variant="subtitle1">
                      {subTitle}
                    </Typography>
                  )}
                </DialogTitle>
              </Box>
            </Box>
          ) : (
            showCloseIcon && closeIconButton
          )}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              display: 'flex',
              flexFlow: 'column nowrap',
            }}
          >
            <Container sx={{ flexGrow: 1 }} disableGutters maxWidth="xs">
              {body}
            </Container>
          </Box>
        </Box>
      ) : (
        <>
          {showCloseIcon && (
            <StyledIconButton size="small" onClick={handleClose}>
              {closeIcon || <FuseSvgIcon>heroicons-solid:x</FuseSvgIcon>}
            </StyledIconButton>
          )}
          {title && (
            <DialogTitle>
              {title}
              {subTitle && <Typography variant="subtitle2">{subTitle}</Typography>}
            </DialogTitle>
          )}
          <DialogContent>{body}</DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default React.memo(ModalComponent);
